import { RichTextV1Schema } from '@mutiny-pkg/champaign-schemas/src/schemas/richText';
import { z } from 'zod';

export enum ColorScheme {
  Light = 'light',
  Dark = 'dark',
}

export enum BlockType {
  Cta = 'Cta',
  EmbeddedSnippet = 'EmbeddedSnippet',
  Embed = 'Embed',
  Footer = 'Footer',
  NavBar = 'NavBar',
  Hero = 'Hero',
  LogoBar = 'LogoBar',
  ValueProp = 'ValueProp',
  ResourceLibrary = 'ResourceLibrary',
  MagicResourceLibrary = 'MagicResourceLibrary',
  Testimonial = 'Testimonial',
  Spotlight = 'Spotlight',
  Head = 'Head',
  NaiveBookMeeting = 'NaiveBookMeeting',
  SmartBookMeeting = 'SmartBookMeeting',
  PagePassword = 'PagePassword',
}

export const BlockTypeSchema = z.nativeEnum(BlockType);

export enum FieldType {
  Image = 'Image',
  Text = 'Text',
  RichText = 'RichText',
  Url = 'Url',
  ImageSize = 'ImageSize',
  ResourceList = 'ResourceList',
  EmbedContent = 'EmbedContent',
  Password = 'Password',
  Boolean = 'Boolean',
}

export const FieldTypeSchema = z.nativeEnum(FieldType);

export enum FieldRole {
  Body = 'Body',
  CtaText = 'CtaText',
  CtaUrl = 'CtaUrl',
  Description = 'Description',
  EmbeddedSnippet = 'EmbeddedSnippet',
  EmbedContent = 'EmbedContent',
  Eyebrow = 'Eyebrow',
  Header = 'Header',
  Preheader = 'Preheader',
  Subheader = 'Subheader',
  Image = 'Image',
  Logo = 'Logo',
  Url = 'Url',
  Head = 'Head',
  Password = 'Password',
  NameCredit = 'NameCredit',
  ResourceList = 'ResourceList',
  Boolean = 'Boolean',
}

export const FieldRoleSchema = z.nativeEnum(FieldRole);

export const BaseFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  role: FieldRoleSchema,
  type: z.nativeEnum(FieldType),
});

export type BaseField = z.infer<typeof BaseFieldSchema>;

export const PlainTextFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.Text),
  defaultValue: z.string(),
});
export type PlainTextField = z.infer<typeof PlainTextFieldSchema>;

export const PasswordInputFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.Password),
  defaultValue: z.string(),
  name: z.string(),
  role: z.literal(FieldRole.Password),
});
export type PasswordInputField = z.infer<typeof PasswordInputFieldSchema>;

export const BooleanFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.Boolean),
  defaultValue: z.enum(['true', 'false']),
  name: z.string(),
  role: z.literal(FieldRole.Boolean),
});

export type BooleanField = z.infer<typeof BooleanFieldSchema>;

export enum ImageSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Xlarge = 'xlarge',
}
export const ImageSizeSchema = z.nativeEnum(ImageSize);
export const ImageSizeFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.ImageSize),
  defaultValue: ImageSizeSchema,
});
export type ImageSizeField = z.infer<typeof ImageSizeFieldSchema>;

export const UrlFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.Url),
  defaultValue: z.string(),
});
export type UrlField = z.infer<typeof UrlFieldSchema>;

export const ImageFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.Image),
  defaultValue: z.string(),
});
export type ImageField = z.infer<typeof ImageFieldSchema>;

export const RichTextFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.RichText),
  defaultValue: RichTextV1Schema,
});
export type RichTextField = z.infer<typeof RichTextFieldSchema>;

export const ResourceSchema = z.object({
  title: z.string().or(RichTextV1Schema),
  description: z.string().or(RichTextV1Schema),
  explanation: z.string().or(RichTextV1Schema).nullable(),
  imageUrl: z.string().nullable(),
  ctaText: z.string().or(RichTextV1Schema),
  ctaUrl: z.string(),
});

export const ResourceListFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.ResourceList),
  defaultValue: z.object({
    size: z.number(),
    resources: z.array(ResourceSchema),
  }),
});

export type ResourceListField = z.infer<typeof ResourceListFieldSchema>;
export enum EmbedContentType {
  Url = 'Url',
  Snippet = 'Snippet',
}
export const EmbedContentTypeSchema = z.nativeEnum(EmbedContentType);

export const EmbedContentFieldSchema = BaseFieldSchema.extend({
  type: z.literal(FieldType.EmbedContent),
  defaultValue: z.object({
    contentType: EmbedContentTypeSchema,
    contentUrl: z.string().nullable(),
    contentSnippet: z.string().nullable(),
    contentWidth: z.string(),
    contentHeight: z.string(),
  }),
});
export type EmbedContentField = z.infer<typeof EmbedContentFieldSchema>;

export const FieldSchema = z.discriminatedUnion('type', [
  PlainTextFieldSchema,
  UrlFieldSchema,
  ImageFieldSchema,
  RichTextFieldSchema,
  ImageSizeFieldSchema,
  ResourceListFieldSchema,
  EmbedContentFieldSchema,
  PasswordInputFieldSchema,
  BooleanFieldSchema,
]);

export type Field = z.infer<typeof FieldSchema>;

export const DefaultLayout = 'standard';

export const FieldGroupSchema = z.object({
  fields: z.array(FieldSchema),
  id: z.string(),
});

export type FieldGroup = z.infer<typeof FieldGroupSchema>;

export const FieldOfType = (type: FieldType) => FieldSchema.refine((field) => field.type === type);

export const FieldLookup = z.object({
  getField: z
    .function()
    .args(z.string(), z.string())
    .returns(z.object({ type: FieldTypeSchema })),
});

export type FieldOfType<T extends FieldType, F = Field> = F extends { type: T } ? F : never;
export const ColorSchemeSchema = z.nativeEnum(ColorScheme);
